import type { CSSProperties } from 'react';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { useStickyBand } from './useStickyBand';
import './elementSticky.css';
import { boolProp, choiceProp, numberProp, textProp } from './elementProps';
import { schemeVars } from './colorSchemes';
import { FoldingColumn } from './FoldingColumn';
import { CatalogSlider, SLIDER_ARROWS } from './CatalogSlider';

// The four boxes everything else sits in. All four are deliberately plain: a `row` is not given
// `display: flex` here, because the element's own styles say what it is and the catalog's defaults
// put them there. A box that painted itself would be a box no merchant could unpaint.
//
// None of the four writes an id of its own: the Custom Id under Attributes is the one every element
// carries, and `useElementBox` already puts it on the box. A second `id` written here used to win the
// prop order and blank it, which was that control doing nothing on exactly these four elements.

/** The five bands a page is built of, exactly as `sectionElement` offers them in the inspector. */
const SECTION_TAGS = ['section', 'header', 'footer', 'main', 'aside'] as const;

/** The two answers to how wide a band's content runs, spelled as the inspector stores them. */
const CONTENT_WIDTHS = ['full', 'boxed'] as const;

/** The narrowest and widest a boxed column may be held to, so a typo cannot make a band unusable. */
const MIN_BOXED = 320;
const MAX_BOXED = 2400;

// Reads the column a boxed band holds its content to, or nothing at all while it runs the full width.
function boxedColumn(props: Parameters<typeof choiceProp>[0]): CSSProperties | null {
  if (choiceProp(props, 'layout', CONTENT_WIDTHS, 'full') !== 'boxed') return null;
  const asked = Math.round(numberProp(props, 'contentWidth', 1210));
  const width = Math.min(MAX_BOXED, Math.max(MIN_BOXED, asked));
  return { width: '100%', maxWidth: `${width}px`, marginInline: 'auto' };
}

/** Whether a band stays put, exactly as `sectionElement` offers it. */
const STICKY = ['none', 'always', 'scroll-up'] as const;

// Draws a band of page, as the landmark the merchant chose. The one node type a `preset` may hang off.
// A boxed band keeps its own full width — its background still runs to both edges — and holds what is
// inside it to a column down the middle, which is the only shape a centred page has ever had. The
// column wears no mark of its own, so the hit test and the drop layer see straight through it to the
// band, and a child dropped inside still lands as the band's own child rather than the column's.
export const SectionElement: ElementComponent = ({ node, children }) => {
  const box = useElementBox(node);
  const Band = choiceProp(node.props, 'tag', SECTION_TAGS, 'section');
  const column = boxedColumn(node.props);
  const sticky = choiceProp(node.props, 'sticky', STICKY, 'none');
  const { shown, scrolled } = useStickyBand(sticky);
  const { theme } = useElementRender();
  const className = [box.className, sticky === 'none' ? '' : 'xe-sticky'].filter(Boolean).join(' ');
  return (
    <Band
      {...box}
      className={className}
      style={schemeVars(textProp(node.props, 'scheme'), theme)}
      data-sticky={sticky === 'none' ? undefined : sticky}
      data-hidden={shown ? undefined : ''}
      data-scrolled={scrolled ? '' : undefined}
    >
      {column ? <div style={column}>{children}</div> : children}
    </Band>
  );
};

// Draws a row: a box whose children are meant to sit beside one another.
export const RowElement: ElementComponent = ({ node, children }) => (
  <div {...useElementBox(node)}>{children}</div>
);

// Draws a column: a box whose children are meant to stack, folded under its heading on a phone when asked.
export const ColumnElement: ElementComponent = ({ node, children }) => {
  const box = useElementBox(node);
  if (boolProp(node.props, 'foldOnPhone')) return <FoldingColumn node={node} box={box}>{children}</FoldingColumn>;
  return <div {...box}>{children}</div>;
};

/** Whether a container slides, exactly as `containerElement` offers it. */
const SLIDES = ['off', 'always', 'phone'] as const;

/** The narrowest share of a phone's width one card may take, so the card a shopper reads is never a sliver. */
const PHONE_SHARE = 80;

// Reads how wide one card runs while a container slides: the merchant's share, and at least four fifths on a phone.
const slideShares = (props: Parameters<typeof numberProp>[0]): CSSProperties => {
  const share = Math.min(100, Math.max(30, numberProp(props, 'slideWidth', 70)));
  return { '--xe-slide-share': `${share}%`, '--xe-slide-phone': `${Math.max(share, PHONE_SHARE)}%` } as CSSProperties;
};

// Draws a container: a box of grouped elements, or a row of them as cards that slide when the merchant asks.
export const ContainerElement: ElementComponent = ({ node, children }) => {
  const box = useElementBox(node);
  const { editing } = useElementRender();
  const slides = choiceProp(node.props, 'slider', SLIDES, 'off');
  if (slides === 'off') return <div {...box}>{children}</div>;
  const track = { ...box, className: [box.className, 'xe-box-slider'].filter(Boolean).join(' '), style: slideShares(node.props) };
  return (
    <CatalogSlider on editing={editing} track={track} phoneOnly={slides === 'phone'} arrows={choiceProp(node.props, 'arrows', SLIDER_ARROWS, 'above')}>
      {children}
    </CatalogSlider>
  );
};
